import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { navLinks, siteName, siteTagline } from "@/mocks/site";

export default function SiteNavbar() {
  const [solid, setSolid] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = useMemo(() => {
    const keyword = query.trim();
    if (!keyword) return [];
    return articles
      .filter(
        (article) =>
          article.title.includes(keyword) ||
          article.excerpt.includes(keyword) ||
          article.tags.some((tag) => tag.includes(keyword)),
      )
      .slice(0, 5);
  }, [query]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const keyword = query.trim();
    if (!keyword) return;
    setSearchOpen(false);
    setMobileOpen(false);
    navigate(`/category?q=${encodeURIComponent(keyword)}`);
  };

  const isHome = location.pathname === "/";
  const barSolid = solid || !isHome;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          barSolid
            ? "border-b border-background-800/80 bg-background-950/95 backdrop-blur-md"
            : "bg-gradient-to-b from-background-950/85 via-background-950/40 to-transparent"
        }`}
      >
        <div className="flex h-[72px] items-center justify-between gap-4 px-4 md:px-8">
          <Link to="/" className="group flex shrink-0 items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-sm border border-accent-500/50 bg-primary-900/40 font-heading text-lg text-accent-300 transition-colors group-hover:border-accent-400">
              戒
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-[17px] tracking-wide text-foreground-50">
                {siteName}
              </span>
              <span className="mt-1.5 font-label text-[9px] uppercase tracking-[0.24em] text-foreground-500">
                {siteTagline}
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `relative whitespace-nowrap py-2 text-sm transition-colors ${
                    isActive
                      ? "text-accent-300"
                      : "text-foreground-300 hover:text-foreground-50"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={`absolute -bottom-0.5 left-0 h-px bg-accent-500 transition-all duration-300 ${
                        isActive ? "w-full" : "w-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div ref={searchRef} className="relative hidden md:block">
              <form onSubmit={submitSearch} className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  placeholder="搜索影评、解析、专题"
                  aria-label="站内搜索"
                  className="h-9 w-44 rounded-full border border-background-700/80 bg-background-900/70 pl-9 pr-3 text-xs text-foreground-100 placeholder:text-foreground-600 focus:border-accent-600/70 focus:outline-none xl:w-60"
                />
              </form>
              {searchOpen && query.trim() ? (
                <div className="absolute right-0 top-12 w-80 overflow-hidden rounded-lg border border-background-700 bg-background-950 shadow-xl shadow-background-950/60">
                  {results.length ? (
                    <ul className="divide-y divide-background-800/80">
                      {results.map((item) => (
                        <li key={item.id}>
                          <Link
                            to={`/detail/${item.id}`}
                            onClick={() => setSearchOpen(false)}
                            className="block px-4 py-3 transition-colors hover:bg-background-900"
                          >
                            <p className="line-clamp-1 text-sm text-foreground-100">
                              {item.title}
                            </p>
                            <p className="mt-1 line-clamp-1 text-[11px] text-foreground-500">
                              {item.excerpt}
                            </p>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="px-4 py-6 text-center text-xs text-foreground-500">
                      没有找到相关内容，换个关键词试试
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={submitSearch}
                    className="flex w-full items-center justify-center gap-1 border-t border-background-800 bg-background-900/60 px-4 py-2.5 text-[11px] text-accent-300 transition-colors hover:bg-background-900"
                  >
                    查看全部搜索结果
                    <i className="ri-arrow-right-line" />
                  </button>
                </div>
              ) : null}
            </div>

            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMobileOpen(true)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-700 text-foreground-200 transition-colors hover:border-accent-600/60 hover:text-accent-300 lg:hidden"
            >
              <i className="ri-menu-line text-lg" />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen ? (
        <div className="fixed inset-0 z-[60] bg-background-950/98 lg:hidden">
          <div className="flex h-[72px] items-center justify-between border-b border-background-800 px-4">
            <span className="font-heading text-base text-foreground-50">{siteName}</span>
            <button
              type="button"
              aria-label="关闭菜单"
              onClick={() => setMobileOpen(false)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-700 text-foreground-200"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
          <div className="h-[calc(100vh-72px)] overflow-y-auto px-4 pb-10 pt-5">
            <form onSubmit={submitSearch} className="relative mb-6">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索影评、解析、专题"
                aria-label="站内搜索"
                className="h-12 w-full rounded-full border border-background-700 bg-background-900 pl-11 pr-4 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-accent-600/70 focus:outline-none"
              />
            </form>
            <nav className="flex flex-col">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center justify-between border-b border-background-800/80 py-4 text-base ${
                      isActive ? "text-accent-300" : "text-foreground-200"
                    }`
                  }
                >
                  {link.label}
                  <i className="ri-arrow-right-s-line text-lg text-foreground-600" />
                </NavLink>
              ))}
            </nav>
            <p className="mt-6 text-xs leading-relaxed text-foreground-600">
              本站为影视资料演示站点，所有内容为虚构示例，不提供影片播放与下载。
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}